// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {
	MACOS_INPUT_MONITORING_PERMISSION_NAME,
	MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME,
	MACOS_SYSTEM_SETTINGS_NAME,
	PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import KeybindManager from '@app/features/app/keybindings/KeybindManager';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import NativePermission from '@app/features/voice/state/NativePermission';
import {
	type NativePermissionResult,
	openNativePermissionSettings,
	requestNativePermission,
} from '@app/features/voice/utils/NativePermissions';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useRef, useState} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Review global hotkey access',
	comment: 'Title for the macOS Input Monitoring permission explanation modal.',
});
const MACOS_SCOPE_DESCRIPTOR = msg({
	message:
		'macOS does not provide a permission for selected hotkeys only. It grants Input Monitoring access to an app as a whole.',
	comment:
		'Explains why the macOS Input Monitoring prompt sounds broad. Keep the meaning precise: macOS grants app-wide Input Monitoring, not per-hotkey access.',
});
const USE_DESCRIPTOR = msg({
	message:
		'Because of that, macOS labels this as permission to receive keystrokes from any application. {productName} uses it only to recognize the global hotkeys you configure, such as mute, deafen, and push-to-talk.',
	comment:
		'Privacy-sensitive explanation in the macOS Input Monitoring modal. {productName} is the app name. Do not weaken the scope of the macOS prompt.',
});
const OPTIONAL_DESCRIPTOR = msg({
	message:
		'You do not need to use global hotkeys. If you choose not to continue, {productName} will keep working and those shortcuts will stay local where possible.',
	comment:
		'Explains that macOS Input Monitoring is optional and declining it does not block normal app use. {productName} is the app name.',
});
const RESTART_AFTER_CHANGE_DESCRIPTOR = msg({
	message:
		'After changing Input Monitoring, fully quit and restart {productName} so system-wide hotkeys can use the new permission.',
	comment: 'Restart instruction in the macOS Input Monitoring CTA modal. {productName} is the app name.',
});
const IS_ENABLED_DESCRIPTOR = msg({
	message:
		'{macosInputMonitoringPermissionName} is enabled. Fully quit and restart {productName} so system-wide hotkeys can use it.',
	comment:
		'Success toast in the macOS Input Monitoring CTA modal. {macosInputMonitoringPermissionName} is the macOS permission name and {productName} is the app name.',
});
const PLEASE_ENABLE_IN_DESCRIPTOR = msg({
	message:
		'Enable {productName} in {macosSystemSettingsName} → {macosPrivacyAndSecuritySettingsName} → {macosInputMonitoringPermissionName}. Then fully quit and restart {productName} so system-wide hotkeys can use it.',
	comment:
		'Body of the macOS Input Monitoring CTA modal. Walks the user through the System Settings path. Placeholders are macOS UI names and the app name; keep OS proper nouns intact.',
});
const SHOW_MACOS_PROMPT_DESCRIPTOR = msg({
	message: 'Show macOS prompt',
	comment:
		'Primary button label in the macOS Input Monitoring modal. This button triggers the native macOS permission prompt.',
});
const OPEN_SYSTEM_SETTINGS_DESCRIPTOR = msg({
	message: 'Open {macosSystemSettingsName}',
	comment:
		'Primary button label in the macOS Input Monitoring modal after permission was denied. {macosSystemSettingsName} is the macOS System Settings app name.',
});
const NOT_NOW_DESCRIPTOR = msg({
	message: 'Not now',
	comment: 'Secondary button label in the macOS Input Monitoring modal.',
});
const NO_CHANGE_DESCRIPTOR = msg({
	message: '{macosInputMonitoringPermissionName} was not changed.',
	comment:
		'Toast shown after the macOS Input Monitoring request returns without a grant or denial. {macosInputMonitoringPermissionName} is the macOS permission name.',
});

interface InputMonitoringCTAModalProps {
	onComplete?: (result: NativePermissionResult) => void;
}

export const InputMonitoringCTAModal: React.FC<InputMonitoringCTAModalProps> = observer(({onComplete}) => {
	const {i18n} = useLingui();
	const [submitting, setSubmitting] = useState(false);
	const initialFocusRef = useRef<HTMLButtonElement | null>(null);
	const status = NativePermission.inputMonitoringStatus;
	const openSettings = async () => {
		await openNativePermissionSettings('input-monitoring');
		ToastCommands.createToast({
			type: 'info',
			timeout: 10000,
			children: i18n._(PLEASE_ENABLE_IN_DESCRIPTOR, {
				productName: PRODUCT_NAME,
				macosSystemSettingsName: MACOS_SYSTEM_SETTINGS_NAME,
				macosPrivacyAndSecuritySettingsName: MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME,
				macosInputMonitoringPermissionName: MACOS_INPUT_MONITORING_PERMISSION_NAME,
			}),
		});
	};
	const handleResult = async (result: NativePermissionResult): Promise<void> => {
		NativePermission.setInputMonitoringStatus(result);
		if (result === 'granted') {
			ToastCommands.createToast({
				type: 'success',
				children: i18n._(IS_ENABLED_DESCRIPTOR, {
					macosInputMonitoringPermissionName: MACOS_INPUT_MONITORING_PERMISSION_NAME,
					productName: PRODUCT_NAME,
				}),
			});
			await KeybindManager.reapplyGlobalShortcuts();
		} else if (result === 'denied') {
			await openSettings();
		} else if (result === 'not-determined') {
			ToastCommands.createToast({
				type: 'info',
				children: i18n._(NO_CHANGE_DESCRIPTOR, {
					macosInputMonitoringPermissionName: MACOS_INPUT_MONITORING_PERMISSION_NAME,
				}),
			});
		}
	};
	const handleContinue = async () => {
		setSubmitting(true);
		let result: NativePermissionResult = status;
		try {
			const current = await NativePermission.recheckInputMonitoring();
			if (current === 'denied') {
				result = current;
				await openSettings();
			} else if (current === 'not-determined') {
				result = await requestNativePermission('input-monitoring');
				await handleResult(result);
			} else {
				result = current;
				await handleResult(result);
			}
			ModalCommands.pop();
			onComplete?.(result);
		} finally {
			setSubmitting(false);
		}
	};
	const handleDismiss = () => {
		NativePermission.dismissInputMonitoringNagbar();
		ModalCommands.pop();
		onComplete?.(status);
	};
	const primaryLabel =
		status === 'denied'
			? i18n._(OPEN_SYSTEM_SETTINGS_DESCRIPTOR, {macosSystemSettingsName: MACOS_SYSTEM_SETTINGS_NAME})
			: i18n._(SHOW_MACOS_PROMPT_DESCRIPTOR);
	return (
		<Modal.Root
			size="small"
			centered
			initialFocusRef={initialFocusRef}
			data-flx="voice.input-monitoring-cta-modal.modal-root"
		>
			<Modal.Header title={i18n._(TITLE_DESCRIPTOR)} data-flx="voice.input-monitoring-cta-modal.modal-header" />
			<Modal.Content data-flx="voice.input-monitoring-cta-modal.modal-content">
				<Modal.ContentLayout data-flx="voice.input-monitoring-cta-modal.modal-content-layout">
					<Modal.Description data-flx="voice.input-monitoring-cta-modal.modal-description">
						{i18n._(MACOS_SCOPE_DESCRIPTOR)}
					</Modal.Description>
					<Modal.Description data-flx="voice.input-monitoring-cta-modal.modal-description--2">
						{i18n._(USE_DESCRIPTOR, {productName: PRODUCT_NAME})}
					</Modal.Description>
					<Modal.Description data-flx="voice.input-monitoring-cta-modal.modal-description--3">
						{i18n._(OPTIONAL_DESCRIPTOR, {productName: PRODUCT_NAME})}
					</Modal.Description>
					<Modal.Description data-flx="voice.input-monitoring-cta-modal.modal-description--4">
						{i18n._(RESTART_AFTER_CHANGE_DESCRIPTOR, {productName: PRODUCT_NAME})}
					</Modal.Description>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer data-flx="voice.input-monitoring-cta-modal.modal-footer">
				<Button onClick={handleDismiss} variant="secondary" data-flx="voice.input-monitoring-cta-modal.button.dismiss">
					{i18n._(NOT_NOW_DESCRIPTOR)}
				</Button>
				<Button
					onClick={handleContinue}
					submitting={submitting}
					variant="primary"
					ref={initialFocusRef}
					data-flx="voice.input-monitoring-cta-modal.button.enable"
				>
					{primaryLabel}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
